// 🔥 Firebase 디바이스 카탈로그 스키마 - 최고급 타입 정의
// [배경] 상수 데이터를 Firebase로 이관하여 SSOT(Single Source of Truth) 구축
//        모든 화면과 관리자 페이지가 동일한 Firebase 소스를 참조하도록 설계

export type DeviceType = 'smartphone' | 'tablet' | 'wearable';
export type DeviceBrand = 'Apple' | 'Samsung' | 'LG';
export type ConnectivityType = 'Wi-Fi' | 'Wi-Fi + Cellular' | 'LTE' | '5G' | 'GPS' | 'Lightning' | 'USB-C' | '유선충전' | '무선충전';

/**
 * Firebase device_catalog 컬렉션 스키마
 * 각 문서는 하나의 기기 모델을 나타내며, 지원하는 모든 구성 옵션을 포함
 */
export interface DeviceCatalogDocument {
  // 🔑 고유 식별자 (결정적 생성)
  id: string;                    // "apple-smartphone-iphone15-pro-max"
  
  // 📱 기본 분류 정보
  deviceType: DeviceType;        // "smartphone", "tablet", "wearable" 
  brand: DeviceBrand;           // "Apple", "Samsung"
  series: string;               // "iPhone 15", "Galaxy S24", "iPad Pro"
  
  // 🏷️ 모델 정보
  modelEn: string;              // "iPhone 15 Pro Max"
  modelKo: string;              // "아이폰15 PRO MAX"
  modelNumber?: string;         // "A2484" (Apple 모델 번호)
  
  // ⚙️ 지원 구성 옵션들 (배열로 모든 옵션 포함)
  storageOptions: string[];     // ["128GB", "256GB", "512GB", "1TB"]
  connectivityOptions?: ConnectivityType[]; // ["Wi-Fi", "Wi-Fi + Cellular"]
  colorOptions?: string[];      // ["스페이스 그레이", "실버", "골드"]
  
  // 🎨 UI 관련 정보
  image: string;                // "/assets/iPhone/iPhone15/iphone15-pro-max.png"
  thumbnails?: Record<string, string>; // 추가 이미지들
  recommended: boolean;         // 추천 모델 여부
  subcategory?: string;         // "iPad Pro", "AirPods Pro" (필터링용)
  
  // 📅 메타데이터
  releaseYear: number;          // 2024
  releaseDate?: string;         // "2024-09-15" (출시일)
  discontinuedDate?: string;    // 단종일 (옵션)
  
  // 🔍 검색 및 매칭용
  aliases: string[];            // ["아이폰15", "iPhone15", "iphone-15-pro-max"]
  searchKeywords?: string[];    // 추가 검색어

  // ❌ 비활성화된 개별 구성(Variant) 목록 (행 단위 삭제용)
  // 예: [{ storage: "128GB", connectivity: "Wi-Fi" }] → 해당 구성은 관리자/사용자 화면에서 숨김
  disabledVariants?: {
    storage: string;
    connectivity?: ConnectivityType;
  }[];
  
  // 🏢 관리 정보
  isActive: boolean;            // 활성화 여부
  priority: number;             // 정렬 우선순위 (0: 최우선)
  
  // 📝 감사(Audit) 정보
  createdAt: Date;
  updatedAt: Date;
  updatedBy: string;            // 관리자 ID
  memo?: string;                // 관리자 메모
}

/**
 * Firebase unified_prices 컬렉션 스키마  
 * 각 문서는 특정 모델+구성의 등급별 가격을 나타냄
 */
export interface UnifiedPriceDocument {
  // 🔑 고유 식별자 (결정적 생성)
  id: string;                   // "apple-smartphone-iphone15-pro-max-256gb-wifi"
  
  // 📱 모델 참조
  modelId: string;              // device_catalog 문서 ID 참조
  
  // 📱 기본 분류 정보 (쿼리 최적화를 위한 중복 저장)
  deviceType: DeviceType;       
  brand: DeviceBrand;
  
  // ⚙️ 구성 변형 (Variant)
  variant: {
    storage: string;            // "256GB"
    connectivity?: ConnectivityType; // "Wi-Fi"
    color?: string;             // "스페이스 그레이"
    caseSize?: string;          // "44mm" (Apple Watch용)
  };
  
  // 🌍 지역/통화 정보
  region: 'KR';                // 한국 전용
  currency: 'KRW';             // 원화
  
  // 💰 등급별 가격 (핵심 데이터)
  gradePrices: {
    A: number;                  // A등급 가격
    B: number;                  // B등급 가격
    C: number;                  // C등급 가격
    D: number;                  // D등급 가격
    E: number;                  // E등급 가격
  };

  // 🏢 관리 정보
  isActive: boolean;            // 활성화 여부
  version: 'draft' | 'live';   // 초안/배포 상태

  // 📊 가격 히스토리 (간단한 추적)
  previousPrice?: {
    A: number; B: number; C: number; D: number; E: number;
    changedAt: Date;
    changedBy: string;
  };
  
  // 📝 감사(Audit) 정보  
  createdAt: Date;
  updatedAt: Date;
  updatedBy: string;
  memo?: string;
  
  // 🔄 마이그레이션 추적 (일시적)
  legacy?: {
    originalCollection: string;  // "grade_prices_apple"
    originalId: string;         // 기존 문서 ID
    migratedAt: Date;
  };
}

/**
 * 디바이스 검색 및 필터링용 옵션
 */
export interface DeviceSearchOptions {
  deviceType?: DeviceType;
  brand?: DeviceBrand;  
  series?: string;
  recommended?: boolean;
  releaseYearFrom?: number;
  releaseYearTo?: number;
  searchQuery?: string;
  limit?: number;
  orderBy?: 'releaseYear' | 'priority' | 'modelKo';
  orderDirection?: 'asc' | 'desc';
}

/**
 * 가격 조회 옵션
 */
export interface PriceSearchOptions {
  deviceType?: DeviceType;
  brand?: DeviceBrand;
  modelId?: string;
  storage?: string;
  connectivity?: ConnectivityType;
  region?: 'KR';
  activeOnly?: boolean;
}

/**
 * 관리자 가격 편집용 인터페이스
 */
export interface PriceEditRequest {
  priceId: string;
  gradePrices: {
    A: number;
    B: number;
    C: number;
    D: number;
    E: number;
  };
  memo?: string;
}

/**
 * 새 모델 추가용 인터페이스
 */
export interface NewDeviceModelRequest {
  deviceType: DeviceType;
  brand: DeviceBrand;
  series: string;
  modelEn: string;
  modelKo: string;
  storageOptions: string[];
  connectivityOptions?: ConnectivityType[];
  image: string;
  recommended?: boolean;
  releaseYear?: number;
}

/**
 * ID 생성 유틸리티 함수들
 * 
 * 🔑 P2 핵심: + / ++ / plus 변형을 반드시 구분하여 보존!
 * - S25 → ...-galaxy-s25
 * - S25+ → ...-galaxy-s25-plus
 * - S25++ → ...-galaxy-s25-plus-plus
 */
export const generateModelId = (model: {
  brand: string;
  deviceType: string; 
  series: string;
  modelEn: string;
}): string => {
  // 🔑 P2 핵심: modelEn에서 + / ++ 를 정규화 토큰으로 변환 (유실 방지!)
  const normalizeModelEn = (modelEn: string): string => {
    let normalized = modelEn
      .trim()
      // 순서 중요: ++ 먼저 처리, 그 다음 + 처리
      .replace(/\+\+/g, '-plus-plus')
      .replace(/\+/g, '-plus')
      // "플러스" 한글도 -plus로 정규화
      .replace(/플러스플러스/gi, '-plus-plus')
      .replace(/플러스/gi, '-plus')
      // 공백 → 하이픈
      .replace(/\s+/g, '-')
      // 소문자
      .toLowerCase()
      // 허용 문자만 (하이픈, 알파벳, 숫자)
      .replace(/[^a-z0-9\-]/g, '')
      // 연속 하이픈 정리
      .replace(/-+/g, '-')
      // 앞뒤 하이픈 제거
      .replace(/^-|-$/g, '');
    
    return normalized;
  };
  
  const normalized = [
    model.brand.toLowerCase(),
    model.deviceType.replace('스마트폰', 'smartphone').replace('태블릿', 'tablet').replace('웨어러블', 'wearable'),
    model.series.toLowerCase().replace(/\s+/g, '-'),
    normalizeModelEn(model.modelEn)
  ].join('-');
  
  return normalized;
};

/**
 * 🔑 priceId 생성 함수 - SSOT 계약 준수
 * 
 * [중요] 디바이스 타입별 규칙:
 * - 스마트폰: connectivity = 항상 'lte' 포함 (강제)
 * - 태블릿: connectivity = 'wifi' | 'cellular' (필수)
 * - 웨어러블: connectivity/storage 포함 금지
 * 
 * 참고: 완전한 canonical 규칙은 src/utils/idCanonical.ts의 buildCanonicalPriceId 참조
 */
export const generatePriceId = (modelId: string, variant: {
  storage: string;
  connectivity?: string;
  color?: string;
}, region: 'KR' = 'KR'): string => {
  const parts = [modelId.toLowerCase()]; // 항상 소문자로 정규화
  
  // 🔥 디바이스 타입 추론 (modelId에서)
  const modelIdLower = modelId.toLowerCase();
  let deviceType: 'smartphone' | 'tablet' | 'wearable' = 'smartphone';
  
  if (modelIdLower.includes('-smartphone-') || modelIdLower.startsWith('smartphone-')) {
    deviceType = 'smartphone';
  } else if (modelIdLower.includes('-tablet-') || modelIdLower.startsWith('tablet-') ||
             modelIdLower.includes('ipad') || modelIdLower.includes('galaxy-tab')) {
    deviceType = 'tablet';
  } else if (modelIdLower.includes('-wearable-') || modelIdLower.startsWith('wearable-') ||
             modelIdLower.includes('airpods') || modelIdLower.includes('watch') || 
             modelIdLower.includes('buds')) {
    deviceType = 'wearable';
  }
  
  // 🔑 웨어러블: 구성(풀세트 등)을 storage로 포함
  if (deviceType === 'wearable') {
    if (variant.storage) {
      const normalizedStorage = variant.storage.toLowerCase().replace(/[^a-z0-9가-힣]/g, '');
      if (normalizedStorage && normalizedStorage !== '기본') {
        parts.push(normalizedStorage);
      }
    }
    parts.push(region.toLowerCase());
    return parts.join('-');
  }
  
  // 🔥 storage 처리 - 빈 문자열이 되는 경우(한글 '기본' 등) 제외
  if (variant.storage) {
    const normalizedStorage = variant.storage.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalizedStorage) {
      parts.push(normalizedStorage);
    }
  }
  
  // 🔑 스마트폰: connectivity는 항상 'lte' 강제
  if (deviceType === 'smartphone') {
    parts.push('lte');
  }
  // 🔑 태블릿: connectivity 처리 - wifi/cellular 구분
  else if (deviceType === 'tablet' && variant.connectivity) {
    const connLower = variant.connectivity.toLowerCase();
    // Cellular 관련 키워드가 있으면 'cellular'로 정규화
    if (connLower.includes('cellular') || connLower.includes('lte') || connLower.includes('5g')) {
      parts.push('cellular');
    } else {
      // Wi-Fi만 있거나 '기본'이면 'wifi' (태블릿은 명시적으로 포함)
      parts.push('wifi');
    }
  } else if (deviceType === 'tablet') {
    // 태블릿인데 connectivity가 없으면 기본값 wifi
    parts.push('wifi');
  }
  
  // 🔥 color 처리 - 빈 문자열이 되는 경우 제외
  if (variant.color) {
    const normalizedColor = variant.color.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
    if (normalizedColor) {
      parts.push(normalizedColor);
    }
  }
  
  parts.push(region.toLowerCase());
  
  return parts.join('-');
};

/**
 * 🔑 Connectivity 정규화 함수 (SSOT 계약)
 * - 'Wi-Fi', '기본', undefined → null (priceId에 미포함)
 * - 'Wi-Fi + Cellular', 'Cellular' → 'cellular' (priceId에 포함)
 */
export type ConnectivityCanonical = 'wifi' | 'cellular' | null;

export const normalizeConnectivity = (input: string | undefined): ConnectivityCanonical => {
  if (!input || input === 'Wi-Fi' || input === '기본' || input.toLowerCase() === 'wifi') {
    return null; // priceId에 미포함
  }
  if (input === 'Wi-Fi + Cellular' || input === 'Cellular' || input.toLowerCase().includes('cellular')) {
    return 'cellular';
  }
  return null;
};

/**
 * 🔑 Storage 정규화 함수 (SSOT 계약)
 * - '기본', 'default': null (priceId에 미포함)
 * - 한글 포함 (웨어러블 구성명): 공백만 제거 (예: '유닛 세트' → '유닛세트')
 * - 그 외: 소문자 + 숫자만 (예: '256 GB' → '256gb')
 */
export const normalizeStorage = (input: string, deviceType?: DeviceType): string | null => {
  // 명시적 기본값 처리
  if (!input || input === '기본' || input.toLowerCase() === 'default' || input.toLowerCase() === 'base') {
    return null;
  }

  // 한글 포함 (웨어러블 구성명 등): 공백만 제거
  if (/[가-힣]/.test(input)) {
    return input.replace(/\s+/g, '') || null;
  }

  // 소문자 + 숫자만 추출 (예: '256 GB' → '256gb')
  return input.toLowerCase().replace(/[^a-z0-9]/g, '') || null;
};

/**
 * 🔑 priceId용 connectivity 문자열 반환 (SSOT 계약)
 * - null → undefined (priceId에 미포함)
 * - 'cellular' → 'cellular' (priceId에 포함)
 */
export const getConnectivityForPriceId = (connectivity: string | undefined): string | undefined => {
  const normalized = normalizeConnectivity(connectivity);
  return normalized === 'cellular' ? 'cellular' : undefined;
};

/**
 * 🔑 세대/버전 번호 패턴 (순번이 아닌 것들)
 * - airpods-1, airpods-2 → 에어팟 1세대, 2세대
 * - iphone-14, iphone-15 → 아이폰 14, 15
 * - watch-8, watch-9 → 애플워치 8, 9
 * - 이런 숫자는 모델의 일부이므로 제거하면 안 됨
 */
const GENERATION_PATTERNS = [
  /airpods-\d+$/i,           // 에어팟 세대
  /airpods-pro-\d+$/i,       // 에어팟 프로 세대
  /airpods-max-\d*$/i,       // 에어팟 맥스
  /watch-\d+$/i,             // 애플워치/갤럭시워치 시리즈
  /watch-ultra-\d*$/i,       // 애플워치 울트라
  /buds-\d+$/i,              // 갤럭시 버즈 시리즈
  /buds-pro-\d*$/i,          // 갤럭시 버즈 프로
  /iphone-\d+$/i,            // 아이폰 시리즈
  /ipad-\d+$/i,              // 아이패드 시리즈
  /galaxy-s\d+$/i,           // 갤럭시 S 시리즈
  /galaxy-z\d+$/i,           // 갤럭시 Z 시리즈
  /galaxy-a\d+$/i,           // 갤럭시 A 시리즈
  /tab-s\d+$/i,              // 갤럭시 탭 S 시리즈
  /tab-a\d+$/i,              // 갤럭시 탭 A 시리즈
];

/**
 * 🔑 끝의 숫자가 세대/버전 번호인지 확인
 */
const isGenerationNumber = (modelId: string): boolean => {
  return GENERATION_PATTERNS.some(pattern => pattern.test(modelId));
};

/**
 * 🔑 modelId가 canonical 형식인지 검증
 * - storage, connectivity, 순번이 포함되면 false
 * - 형식: brand-deviceType-series-model
 * - 단, 세대 번호(airpods-1, watch-9 등)는 허용
 */
export const isCanonicalModelId = (modelId: string): boolean => {
  // storage 패턴 검사 (예: -256gb-, -1tb-)
  if (/-\d+(gb|tb)-/i.test(modelId)) return false;
  
  // 끝에 순번이 있는 경우 (예: -139)
  // 단, 세대 번호(airpods-1, watch-9 등)는 허용
  if (/-\d+$/.test(modelId) && !/\d+(gb|tb|inch|mm)$/i.test(modelId)) {
    // 세대/버전 번호 패턴이면 canonical로 인정
    if (isGenerationNumber(modelId)) {
      return true;
    }
    // 3자리 이상 숫자는 확실히 순번
    const trailingNum = modelId.match(/-(\d+)$/)?.[1];
    if (trailingNum && trailingNum.length >= 3) {
      return false;
    }
    // 그 외의 경우는 문맥에 따라 판단 (기본적으로 순번으로 간주)
    return false;
  }
  
  // connectivity 패턴 검사 (예: -wifi-, -cellular-)
  if (/-wifi-|-cellular-/i.test(modelId)) return false;
  
  return true;
};

/**
 * 🔑 오염된 modelId에서 순수 modelId 추출
 * - storage, connectivity, 순번 제거
 * - 예: "samsung-tablet-...-256gb-wifi-139" → "samsung-tablet-..."
 * - 단, 세대 번호(airpods-1, watch-9 등)는 유지
 */
export const extractPureModelId = (modelId: string): string => {
  let pureId = modelId;
  
  // 0단계: 이미 canonical이면 그대로 반환 (세대 번호 보호)
  if (isCanonicalModelId(modelId)) {
    return modelId;
  }
  
  // 1단계: 끝에 있는 순번 제거 (예: -139)
  // 단, 모델명 끝에 있는 숫자(inch, mm, gb 등) 또는 세대 번호는 유지
  pureId = pureId.replace(/-(\d+)$/, (match, num) => {
    // 세대/버전 번호 패턴이면 유지
    if (isGenerationNumber(pureId)) {
      return match;
    }
    // 숫자가 3자리 이상이면 순번으로 판단
    if (num.length >= 3) return '';
    // 숫자가 2자리 이하이고 이전에 단위가 없으면 순번으로 판단
    const beforeMatch = pureId.slice(0, pureId.lastIndexOf(match));
    if (!/\d+(gb|tb|inch|mm|세대)$/i.test(beforeMatch)) return '';
    return match; // 유지
  });
  
  // 2단계: 끝에 있는 connectivity 제거 (예: -wifi, -cellular)
  pureId = pureId.replace(/-(wifi|cellular)$/i, '');
  
  // 3단계: 끝에 있는 storage 제거 (예: -256gb, -1tb)
  pureId = pureId.replace(/-(\d+)(gb|tb)$/i, '');
  
  // 4단계: 중간에 있는 storage-connectivity 패턴도 제거
  // 예: -128gb-wifi-12 → 제거
  const storageConnMatch = pureId.match(/-(\d+)(gb|tb)-(wifi|cellular)?-?\d*$/i);
  if (storageConnMatch) {
    const matchIndex = pureId.toLowerCase().indexOf(storageConnMatch[0].toLowerCase());
    if (matchIndex !== -1) {
      pureId = pureId.substring(0, matchIndex);
    }
  }
  
  return pureId;
};

/**
 * 검색어 생성 유틸리티
 */
export const generateAliases = (model: {
  modelEn: string;
  modelKo: string;
  series: string;
}): string[] => {
  const aliases = new Set<string>();
  
  // 기본 모델명들
  aliases.add(model.modelEn.toLowerCase());
  aliases.add(model.modelKo.toLowerCase());
  aliases.add(model.series.toLowerCase());
  
  // 변형들
  aliases.add(model.modelEn.replace(/\s+/g, '').toLowerCase());
  aliases.add(model.modelKo.replace(/\s+/g, '').toLowerCase());
  aliases.add(model.modelEn.replace(/\s+/g, '-').toLowerCase());
  
  // 시리즈별 특수 처리
  if (model.series.includes('iPhone')) {
    const num = model.series.match(/iPhone\s*(\d+)/);
    if (num) {
      aliases.add(`아이폰${num[1]}`);
      aliases.add(`iphone${num[1]}`);
    }
  }
  
  if (model.series.includes('Galaxy')) {
    aliases.add(model.series.replace('Galaxy', '갤럭시'));
  }
  
  return Array.from(aliases).filter(Boolean);
};
